import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoPipe } from '@jsverse/transloco';
import { produce } from 'immer';
import { GuidelineService } from 'shared/domain';
import { IconDirective, ButtonDirective } from 'shared/ui-common';

import { ComponentWidget, GuidelineActionComponent, GuidelineActionSignificance } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { DividerComponent } from '../../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { TitleDescriptionComponent } from '../../../chapter-sidebar-wrapper/components/title-description/title-description.component';
import {
  GuidelineSidebarBasics,
  GuidelineSidebarBasicsComponent,
} from '../../../guideline-sidebar-basics/guideline-sidebar-basics.component';
import { SidebarMedicationsComponent } from '../../../sub-components/sidebar-medications/sidebar-medications.component';
import { SignificancePreferenceComponent } from '../../../sub-components/significance-preference/significance-preference.component';
import { TechnicalInfoComponent } from '../../../sub-components/technical-info/technical-info.component';

@Component({
  standalone: true,
  imports: [
    MatTabsModule,

    TranslocoPipe,
    SidebarMedicationsComponent,
    TitleDescriptionComponent,
    SignificancePreferenceComponent,
    TechnicalInfoComponent,
    MatIconModule,
    IconDirective,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
    ButtonDirective,
  ],
  selector: 'guideline-component-sidebar-action',
  templateUrl: './guideline-component-sidebar-action.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./guideline-component-sidebar-action.component.scss'],
})
export class GuidelineComponentSidebarActionComponent {
  private guidelineService = inject(GuidelineService);
  public widget = input<ComponentWidget<GuidelineActionComponent>>();

  protected showAllMedications = signal(false);

  protected toggleShowAllMedications(event): void {
    this.showAllMedications.set(event);
  }

  protected handleTitleChanged(title: string): void {
    const newWidget = produce(this.widget(), (draft) => {
      draft.component.title = title;
    });

    this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
  }

  protected onSignificancePreferenceChanged(
    $event: Partial<{
      significance: GuidelineActionSignificance;
      preferredOptionOrder: number;
    }>,
  ) {
    const newWidget = produce(this.widget(), (draft) => {
      if ($event.significance !== undefined) {
        draft.component.significance = $event.significance;
      }

      if ($event.preferredOptionOrder !== undefined) {
        draft.component.preferredOptionOrder = $event.preferredOptionOrder;
      }
    });

    this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
  }

  protected onMedicationChanged(medicationRequests: GuidelineActionComponent[]) {
    const newWidget = produce(this.widget(), (draft) => {
      draft.component.medicationRequests = medicationRequests;
    });

    this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newWidget = produce(this.widget(), (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
      draft.resourceTags = basics.resourceTags;
    });

    this.guidelineService.updateWidget(newWidget.uniqueId, newWidget);
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
}
